import { useEffect, useCallback, useRef } from 'react';
import { Compass, ZoomIn, Edit3, Command } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type ShortcutCategory = 'general' | 'navigation' | 'editing' | 'zoom';

export interface ShortcutConfig {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: () => void;
  description: string;
  category: ShortcutCategory;
  enabled?: boolean;
}

export interface ShortcutDefinition {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  category: ShortcutCategory;
}

// Helper to check if focus is on an input element
function isInputElementFocused(): boolean {
  const activeElement = document.activeElement as HTMLElement;
  if (!activeElement) return false;
  
  const tagName = activeElement.tagName.toLowerCase();
  const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  const isEditable = activeElement.isContentEditable;
  const isTextInput = tagName === 'input' && 
    ['text', 'password', 'email', 'number', 'search', 'tel', 'url'].includes(
      (activeElement as HTMLInputElement).type || 'text'
    );
  
  return isInput || isEditable || isTextInput;
}

// Helper to format shortcut for display
export function formatShortcut(shortcut: ShortcutDefinition): string {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const ctrlKey = isMac ? '⌘' : 'Ctrl';
  
  const parts: string[] = [];
  
  if (shortcut.ctrl) {
    parts.push(ctrlKey);
  }
  if (shortcut.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (shortcut.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }
  
  // Format key name
  let keyName = shortcut.key.toUpperCase();
  if (keyName === ' ') keyName = 'Space';
  if (keyName === 'ARROWLEFT') keyName = '←';
  if (keyName === 'ARROWRIGHT') keyName = '→';
  if (keyName === 'ARROWUP') keyName = '↑';
  if (keyName === 'ARROWDOWN') keyName = '↓';
  if (keyName === 'ESCAPE') keyName = 'Esc';
  if (keyName === 'ENTER') keyName = 'Enter';
  if (keyName === 'DELETE') keyName = 'Del';
  if (keyName === 'BACKSPACE') keyName = '⌫';
  if (keyName === 'PLUS' || keyName === '=') keyName = '+';
  if (keyName === 'MINUS' || keyName === '-') keyName = '-';
  
  parts.push(keyName);
  
  return parts.join(isMac ? '' : '+');
}

// Main hook for keyboard shortcuts
export function useKeyboardShortcuts(
  shortcuts: ShortcutConfig[],
  enabled: boolean = true
) {
  const shortcutsRef = useRef(shortcuts);
  
  // Keep shortcuts ref updated
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);
  
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Check if shortcuts are globally enabled
    if (!enabled) return;
    
    // Ignore if focus is on input/textarea
    if (isInputElementFocused()) {
      // But allow Escape key to blur the input
      if (e.key !== 'Escape') {
        return;
      }
    }
    
    // Process shortcuts
    for (const shortcut of shortcutsRef.current) {
      // Check if shortcut is enabled (default: true)
      if (shortcut.enabled === false) continue;
      
      // Check modifiers
      const ctrlMatch = shortcut.ctrl 
        ? (e.ctrlKey || e.metaKey) 
        : !e.ctrlKey && !e.metaKey;
      const shiftMatch = shortcut.shift 
        ? e.shiftKey 
        : !e.shiftKey;
      const altMatch = shortcut.alt 
        ? e.altKey 
        : !e.altKey;
      
      // Check key match (case-insensitive)
      const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase() ||
        e.code.toLowerCase() === shortcut.key.toLowerCase();
      
      if (ctrlMatch && shiftMatch && altMatch && keyMatch) {
        e.preventDefault();
        e.stopPropagation();
        shortcut.action();
        return;
      }
    }
  }, [enabled]);
  
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

// Hook for a single shortcut
export function useKeyboardShortcut(
  key: string,
  action: () => void,
  options: {
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    enabled?: boolean;
  } = {}
) {
  const { ctrl = false, shift = false, alt = false, enabled = true } = options;
  
  useKeyboardShortcuts(
    [{ key, ctrl, shift, alt, action, description: '', category: 'general', enabled }],
    enabled
  );
}

// All available shortcuts definitions (for help display)
// Only working shortcuts that don't conflict with browser
export const SHORTCUT_DEFINITIONS: ShortcutDefinition[] = [
  // General
  { key: '/', ctrl: true, description: 'Показати гарячі клавіші', category: 'general' },
  { key: 'Escape', description: 'Закрити / Скасувати', category: 'general' },
  
  // Navigation
  { key: 'ArrowUp', description: 'Попередня подія', category: 'navigation' },
  { key: 'ArrowDown', description: 'Наступна подія', category: 'navigation' },
  { key: 'ArrowLeft', description: 'Попередній макет', category: 'navigation' },
  { key: 'ArrowRight', description: 'Наступний макет', category: 'navigation' },
  
  // Zoom - only on canvas
  { key: '=', ctrl: true, description: 'Збільшити (на Canvas)', category: 'zoom' },
  { key: '-', ctrl: true, description: 'Зменшити (на Canvas)', category: 'zoom' },
];

// Get shortcuts by category
export function getShortcutsByCategory(category: ShortcutCategory): ShortcutDefinition[] {
  return SHORTCUT_DEFINITIONS.filter(s => s.category === category);
}

// Category labels for display
export const CATEGORY_LABELS: Record<ShortcutCategory, string> = {
  general: 'Загальні',
  navigation: 'Навігація',
  editing: 'Редагування',
  zoom: 'Масштаб',
};

// Category icons for display
export const CATEGORY_ICONS: Record<ShortcutCategory, LucideIcon> = {
  general: Command,
  navigation: Compass,
  editing: Edit3,
  zoom: ZoomIn,
};

// Category colors for display
export const CATEGORY_COLORS: Record<
  ShortcutCategory,
  { bg: string; text: string; border: string }
> = {
  general: {
    bg: 'bg-purple-500/10',
    text: 'text-purple-500',
    border: 'border-purple-500/30',
  },
  navigation: {
    bg: 'bg-blue-500/10',
    text: 'text-blue-500',
    border: 'border-blue-500/30',
  },
  editing: {
    bg: 'bg-amber-500/10',
    text: 'text-amber-500',
    border: 'border-amber-500/30',
  },
  zoom: {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-500',
    border: 'border-emerald-500/30',
  },
};
